// Haalt (gebatcht) de gezondheidsdata voor de zichtbare lopende projecten op:
// - fase/periode/uren via de slanke route /Api/projects/gezondheid (geen bedragen,
//   geen extra_data). Geeft die 404/405 (engine draait de route nog niet — een
//   oude engine kent alleen PATCH /Api/projects/{id} en antwoordt 405), dan tonen
//   we de gezondheid zonder Gripp-uren — bewust GEEN terugval op het brede
//   /api/v1/gripp/projects;
// - open taken/deliverables met een datum via /api/v1/airtable/deadlines.
// Succes en fout worden per verzoek bijgehouden: projecten uit een mislukt
// verzoek blijven 'onbekend' (niet groen) en kunnen opnieuw worden opgevraagd.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AI_ENGINE_URL, BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import {
  grippUitGezondheid, voegToeAanIndex, voegChunksSamen, vandaagDag,
} from './projectGezondheid';

const IDS_PER_VERZOEK = 80; // houdt de querystring ruim onder de URL-limiet

function inStukken(ids) {
  const uit = [];
  for (let i = 0; i < ids.length; i += IDS_PER_VERZOEK) uit.push(ids.slice(i, i + IDS_PER_VERZOEK));
  return uit;
}

// Lijst bijwerken: `klaar` eruit, `fout` erin.
const werkLijstBij = (lijst, klaar, fout) => {
  const weg = new Set(klaar);
  return [...new Set([...lijst.filter((id) => !weg.has(id)), ...fout])];
};

// → { samenvattingen, planning, gelukt: bool, mislukt: ids[], nietBeschikbaar: bool }
// planning = { [projectId]: {eersteDraaidag, aantal, vroegste, taken} } — alleen
// projecten waar postproductie vóór de eerste draaidag staat (wiki 26 sep).
async function haalGripp(ids) {
  const samenvattingen = [];
  const planning = {};
  const mislukt = [];
  let nietBeschikbaar = false;
  let gelukt = false;
  for (const chunk of inStukken(ids)) {
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/projects/gezondheid?ids=${encodeURIComponent(chunk.join(','))}`);
      if (r.status === 404 || r.status === 405) { nietBeschikbaar = true; break; }
      if (!r.ok) { mislukt.push(...chunk); continue; }
      const d = await r.json();
      samenvattingen.push(...(d.projecten || []).map(grippUitGezondheid));
      Object.assign(planning, d.planning || {});
      gelukt = true;
    } catch { mislukt.push(...chunk); }
  }
  return { samenvattingen, planning, gelukt, mislukt, nietBeschikbaar };
}

async function haalDeadlines(ids) {
  const chunks = [];
  for (const chunk of inStukken(ids)) {
    try {
      const r = await authFetch(`${AI_ENGINE_URL}/api/v1/airtable/deadlines?days_ahead=365&include_overdue=true&project_ids=${encodeURIComponent(chunk.join(','))}`);
      chunks.push({ ids: chunk, items: r.ok ? ((await r.json()).byDate || []) : null });
    } catch { chunks.push({ ids: chunk, items: null }); }
  }
  return voegChunksSamen(chunks, vandaagDag());
}

/**
 * @param {string[]} projectIds  lopende projecten (geen leads/archief)
 * @returns {{ gripp, deadlines, planning, laden, mislukt, grippNietBeschikbaar, opnieuw }}
 *   deadlines[id] === undefined → onbekend (nog niet of niet opgehaald)
 *   mislukt: { deadlines: string[], gripp: string[] } — opnieuw() vraagt ze opnieuw op
 */
export default function useProjectGezondheid(projectIds) {
  const [gripp, setGripp] = useState(null);
  const [deadlines, setDeadlines] = useState(null);
  const [planning, setPlanning] = useState({});
  const [laden, setLaden] = useState({ gripp: true, deadlines: true });
  const [mislukt, setMislukt] = useState({ deadlines: [], gripp: [] });
  const [grippNietBeschikbaar, setGrippNietBeschikbaar] = useState(false);
  const [poging, setPoging] = useState(0);
  const opgehaald = useRef({ gripp: new Set(), deadlines: new Set() });
  const geenGripp = useRef(false);
  const sleutel = useMemo(() => [...new Set(projectIds)].sort().join(','), [projectIds]);

  useEffect(() => {
    const alle = sleutel ? sleutel.split(',') : [];
    // Geen annuleer-vlag: een lopend verzoek moet zijn resultaat altijd kwijt
    // kunnen, anders blijven die ids 'opgehaald' zonder data.
    const nieuwD = alle.filter((id) => !opgehaald.current.deadlines.has(id));
    if (!nieuwD.length) setLaden((l) => ({ ...l, deadlines: false }));
    else {
      nieuwD.forEach((id) => opgehaald.current.deadlines.add(id));
      setLaden((l) => ({ ...l, deadlines: true }));
      haalDeadlines(nieuwD).then(({ perProject, mislukt: fout }) => {
        fout.forEach((id) => opgehaald.current.deadlines.delete(id));
        setDeadlines((prev) => ({ ...(prev || {}), ...perProject }));
        setMislukt((m) => ({ ...m, deadlines: werkLijstBij(m.deadlines, nieuwD, fout) }));
        setLaden((l) => ({ ...l, deadlines: false }));
      });
    }

    const nieuwG = geenGripp.current ? [] : alle.filter((id) => !opgehaald.current.gripp.has(id));
    if (!nieuwG.length) setLaden((l) => ({ ...l, gripp: false }));
    else {
      nieuwG.forEach((id) => opgehaald.current.gripp.add(id));
      setLaden((l) => ({ ...l, gripp: true }));
      haalGripp(nieuwG).then(({ samenvattingen, planning: waarschuwingen, gelukt, mislukt: fout, nietBeschikbaar }) => {
        if (nietBeschikbaar) { geenGripp.current = true; setGrippNietBeschikbaar(true); }
        fout.forEach((id) => opgehaald.current.gripp.delete(id));
        // Ook zonder treffers een (lege) index: "geen Gripp-project" ≠ "Gripp onbereikbaar".
        if (gelukt) setGripp((prev) => voegToeAanIndex(prev, samenvattingen));
        if (gelukt) {
          // Opnieuw gevraagde projecten zonder waarschuwing: oude weg.
          setPlanning((prev) => {
            const uit = { ...prev };
            nieuwG.forEach((id) => { if (!fout.includes(id)) delete uit[id]; });
            return { ...uit, ...waarschuwingen };
          });
        }
        setMislukt((m) => ({ ...m, gripp: werkLijstBij(m.gripp, nieuwG, fout) }));
        setLaden((l) => ({ ...l, gripp: false }));
      });
    }
    return undefined;
  }, [sleutel, poging]);

  const opnieuw = useCallback(() => setPoging((n) => n + 1), []);

  return { gripp, deadlines, planning, laden, mislukt, grippNietBeschikbaar, opnieuw };
}
